import React, { useEffect, useRef } from 'react';
import { ChartEngine } from '../../lib/crypto/ChartEngine';
import { makeIndicator } from '../../lib/crypto/indicators/plugins';
import { makeDecisionZonesPlugin } from '../../lib/crypto/indicators/decisionZones';
import { ema as emaCalc } from '../../lib/crypto/indicators/math';

/**
 * ChartCanvas — thin React wrapper around ChartEngine. All chart
 * mutations are done imperatively through the engine ref so React
 * re-renders don't churn the WebGL canvas.
 *
 * Props:
 *   candles          — array of {time, open, high, low, close, volume}
 *   type             — 'candles' | 'line' | 'area' | 'heikin_ashi'
 *   indicators       — { ema20:bool, ema50:bool, ema200:bool, vwap:bool }
 *   onEngineReady    — receives the engine instance once mounted
 */
export default function ChartCanvas({ candles, type = 'candles', indicators = {}, indicatorColors = null, zones = null, pan2d = false, onEngineReady, onVisibleRangeChange, onCrosshairMove, onLoadOlder }) {
  const hostRef = useRef(null);
  const engineRef = useRef(null);
  const readyRef = useRef(false);
  const prevCandlesRef = useRef([]);
  const [ohlc, setOhlc] = React.useState(null);   // hovered candle + change%
  // EMA legend — computed from candles + indicator toggles. Show
  // every toggled EMA. When there isn't enough history for a period
  // (e.g. EMA500 on a 200-bar view) surface a "…" placeholder rather
  // than dropping the label — operators kept asking why EMA100-500
  // "didn't pop up" when they toggled them (turned out to be the
  // silent filter). This makes toggle→label deterministic.
  const emaLegend = React.useMemo(() => {
    if (!candles?.length) return [];
    const active = ['ema9', 'ema20', 'ema21', 'ema50', 'ema100', 'ema200', 'ema300', 'ema500']
      .filter((k) => indicators[k]);
    if (!active.length) return [];
    const colorMap = {
      ema9:'#00ff88', ema20:'#00e5ff', ema21:'#ff9500', ema50:'#f7c948',
      ema100:'#ff7ac6', ema200:'#a78bfa', ema300:'#60a5fa', ema500:'#fbbf24',
    };
    const rows = [];
    for (const k of active) {
      const period = parseInt(k.replace('ema', ''), 10);
      const seq = candles.length >= period ? emaCalc(candles, period) : [];
      const last = seq.length ? seq[seq.length - 1].value : null;
      rows.push({ k, color: colorMap[k], value: last, period, warming: last == null });
    }
    return rows;
  }, [candles, indicators]);

  // Latest callback refs — used inside the mount effect below so a
  // parent re-render (fresh onCrosshairMove/onLoadOlder closures)
  // doesn't require a chart re-mount.
  const cbRef = useRef({ onCrosshairMove, onLoadOlder });
  useEffect(() => { cbRef.current = { onCrosshairMove, onLoadOlder }; }, [onCrosshairMove, onLoadOlder]);

  // Mount / unmount the engine.
  useEffect(() => {
    if (!hostRef.current) return;
    const engine = new ChartEngine(hostRef.current, { type });
    engineRef.current = engine;
    readyRef.current = true;
    onEngineReady?.(engine);
    // OHLC hover — subscribe to crosshair move on the chart.
    const chart = engine.provider?.chart;
    let unsubHover = () => {};
    let unsubRange = () => {};
    if (chart) {
      const handler = (param) => {
        // ── OHLC hover tooltip ──
        if (!param?.time) { setOhlc(null); }
        else {
          const priceSeries = engine.provider?.priceSeries;
          const volumeSeries = engine.provider?.volumeSeries;
          if (priceSeries) {
            const d = param.seriesData?.get(priceSeries);
            if (d && d.open != null && d.close != null) {
              const volRow = volumeSeries ? param.seriesData?.get(volumeSeries) : null;
              const volume = volRow?.value ?? 0;
              const chg = d.open ? ((d.close - d.open) / d.open) * 100 : 0;
              setOhlc({
                time: param.time,
                open: d.open, high: d.high, low: d.low, close: d.close,
                volume, changePct: chg,
              });
            } else {
              setOhlc(null);
            }
          } else {
            setOhlc(null);
          }
        }
        // ── Emit crosshair to the parent so IndicatorPanes can
        //    draw their own synchronised vertical guide (G2 B.2). ──
        if (cbRef.current.onCrosshairMove) {
          cbRef.current.onCrosshairMove(param?.time ?? null);
        }
      };
      chart.subscribeCrosshairMove(handler);
      unsubHover = () => { try { chart.unsubscribeCrosshairMove(handler); } catch { /* noop */ } };

      if (onVisibleRangeChange) {
        const rangeH = (range) => onVisibleRangeChange(range);
        chart.timeScale().subscribeVisibleTimeRangeChange(rangeH);
        unsubRange = () => { try { chart.timeScale().unsubscribeVisibleTimeRangeChange(rangeH); } catch { /* noop */ } };
      }

      // ── Infinite historical scroll trigger (G2 B.1) ──
      // When the leftmost visible logical index approaches zero the
      // user has scrolled to the edge of loaded history. Ask the
      // parent to fetch an older page and prepend it. Debounced via
      // an in-flight flag so a single scroll doesn't stampede.
      {
        const inFlightRef = { current: false };
        const logicalH = (logical) => {
          if (!logical || logical.from == null) return;
          const cb = cbRef.current.onLoadOlder;
          if (!cb) return;
          if (logical.from < 50 && !inFlightRef.current) {
            inFlightRef.current = true;
            Promise.resolve(cb())
              .finally(() => {
                // Small cool-off so we don't retrigger on the same frame.
                setTimeout(() => { inFlightRef.current = false; }, 300);
              });
          }
        };
        chart.timeScale().subscribeVisibleLogicalRangeChange(logicalH);
        const prevUnsubRange = unsubRange;
        unsubRange = () => {
          prevUnsubRange();
          try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(logicalH); } catch { /* noop */ }
        };
      }
    }
    return () => {
      readyRef.current = false;
      engineRef.current = null;
      unsubHover(); unsubRange();
      engine.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set data — with a fast-path for real-time WS ticks.
  //
  // If only the LAST candle's OHLC changed (same length, same times
  // for all bars), we skip `engine.setData()` (a full replace that
  // rebuilds every indicator) and use `engine.updateLive(candle)`
  // instead — an O(1) patch that lightweight-charts + our plugins
  // treat as an in-place update. Fully-new datasets (symbol swap,
  // timeframe swap, prepended history from infinite scroll) still
  // take the full setData() path so the chart re-fits cleanly.
  useEffect(() => {
    const engine = engineRef.current;
    if (!readyRef.current || !engine) return;
    const next = candles || [];
    const prev = prevCandlesRef.current;
    prevCandlesRef.current = next;
    if (prev.length && next.length && prev.length === next.length) {
      let same = true;
      for (let i = 0; i < prev.length - 1; i++) {
        if (prev[i].time !== next[i].time) { same = false; break; }
      }
      // Also require the anchor bar to match to catch prepended
      // history (which shifts every index).
      if (same && prev[prev.length - 1].time === next[next.length - 1].time) {
        engine.updateLive(next[next.length - 1]);
        return;
      }
    }
    engine.setData(next);
  }, [candles]);

  // Chart type.
  useEffect(() => {
    if (readyRef.current && engineRef.current) engineRef.current.setChartType(type);
  }, [type]);

  // 2D pan toggle — allow full vertical + horizontal drag when enabled.
  useEffect(() => {
    if (readyRef.current && engineRef.current) engineRef.current.setPan2D(!!pan2d);
  }, [pan2d]);

  // Reconcile indicators — attach / detach plugins as toggles change.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    // Only overlay-style indicators live on the main chart. Pane
    // indicators (rsi/macd/atr/adx) render outside via IndicatorPane.
    const overlays = ['ema9', 'ema20', 'ema21', 'ema50', 'ema100', 'ema200', 'ema300', 'ema500', 'vwap', 'bb', 'dbb', 'kc', 'supertrend'];
    const desired = new Set(Object.entries(indicators || {}).filter(([_, v]) => v).map(([k]) => k));
    for (const name of engine.listPlugins()) {
      if (overlays.includes(name) && !desired.has(name)) engine.removePlugin(name);
    }
    // Attach any newly-desired indicators. If `indicatorColors` is
    // populated we pass it through for the customisable overlays
    // (DBB/KC) so operator-picked palettes take effect immediately.
    for (const key of desired) {
      if (!overlays.includes(key)) continue;
      if (!engine.getPlugin(key)) {
        const overrides = (key === 'dbb' || key === 'kc') ? indicatorColors : null;
        const p = makeIndicator(key, overrides);
        if (p) engine.addPlugin(p);
      }
    }
  }, [indicators, indicatorColors]);

  // When the operator saves a new palette via the modal we release
  // the affected plugins and re-attach with the fresh colours. This
  // is the ONE place where a colour change re-instantiates the
  // series — every other prop change is a hot update.
  const prevColorsRef = useRef(indicatorColors);
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    if (prevColorsRef.current === indicatorColors) return;
    prevColorsRef.current = indicatorColors;
    for (const k of ['dbb', 'kc']) {
      if (engine.getPlugin(k)) {
        engine.removePlugin(k);
        const p = makeIndicator(k, indicatorColors);
        if (p) engine.addPlugin(p);
      }
    }
  }, [indicatorColors]);

  // Phase 53.A — AI decision zones. Rebuild the plugin whenever the
  // analysis result changes so stale zones don't linger on a new coin.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.removePlugin('decision_zones');
    if (Array.isArray(zones) && zones.length) {
      engine.addPlugin(makeDecisionZonesPlugin(zones));
    }
  }, [zones]);

  // When the operator isn't hovering the chart, surface the LAST
  // candle's OHLC so the top-left header always reads like TradingView.
  // Placeholder detection: Binance klines (both REST and WS) can
  // emit "opening placeholder" bars for the current forming minute
  // (O===H===L===C, V=0) OR thin-print bars where a single tiny
  // trade produced V>0 but O===H===L===C still holds. Rendering
  // either type makes the header appear frozen. We walk backward
  // through the tail until we find a bar with a real O/H/L/C
  // spread — guaranteed real trades.
  const displayOhlc = React.useMemo(() => {
    if (ohlc) return ohlc;
    if (!candles?.length) return null;
    const isPlaceholder = (bar) =>
      !!bar &&
      bar.open === bar.high && bar.high === bar.low && bar.low === bar.close;
    // Walk backward until a real bar. If everything is a placeholder
    // (extremely rare — e.g. dead coin, no trades for hours), fall
    // back to the last candle anyway so the header isn't blank.
    let idx = candles.length - 1;
    while (idx > 0 && isPlaceholder(candles[idx])) idx -= 1;
    const last = candles[idx];
    if (!last || last.open == null) return null;
    const chg = last.open ? ((last.close - last.open) / last.open) * 100 : 0;
    return { ...last, changePct: chg };
  }, [ohlc, candles]);

  return (
    <div
      ref={hostRef}
      className="w-full h-full min-h-[420px] bg-black relative"
      data-testid="crypto-chart-canvas"
    >
      {displayOhlc && (
        <div
          data-testid="crypto-chart-ohlc"
          className="absolute top-1 left-1 z-10 pointer-events-none flex items-center gap-2 bg-black/85 border border-cyan-sys/40 px-2 py-0.5 text-[10px] font-mono tabular-nums"
        >
          <span className="text-[var(--txt-muted)]">O</span><span className="text-[var(--txt-primary)]">{displayOhlc.open.toFixed(displayOhlc.close < 1 ? 5 : 2)}</span>
          <span className="text-[var(--txt-muted)]">H</span><span className="text-buy">{displayOhlc.high.toFixed(displayOhlc.close < 1 ? 5 : 2)}</span>
          <span className="text-[var(--txt-muted)]">L</span><span className="text-sell">{displayOhlc.low.toFixed(displayOhlc.close < 1 ? 5 : 2)}</span>
          <span className="text-[var(--txt-muted)]">C</span><span className="text-[var(--txt-primary)]">{displayOhlc.close.toFixed(displayOhlc.close < 1 ? 5 : 2)}</span>
          <span className={displayOhlc.changePct >= 0 ? 'text-buy' : 'text-sell'}>
            {displayOhlc.changePct >= 0 ? '+' : ''}{displayOhlc.changePct.toFixed(2)}%
          </span>
          <span className="text-[var(--txt-muted)]">V</span>
          <span className="text-[var(--txt-primary)]">{displayOhlc.volume >= 1e6 ? `${(displayOhlc.volume/1e6).toFixed(2)}M` : displayOhlc.volume >= 1e3 ? `${(displayOhlc.volume/1e3).toFixed(2)}K` : displayOhlc.volume?.toFixed(2)}</span>
        </div>
      )}
      {emaLegend.length > 0 && (
        <div
          data-testid="crypto-ema-legend"
          className="absolute top-7 left-1 z-10 pointer-events-none flex items-center flex-wrap gap-x-3 gap-y-0.5 bg-black/70 border border-[var(--border-default)] px-2 py-0.5 text-[10px] font-mono tabular-nums"
        >
          {emaLegend.map((e) => (
            <span key={e.k} style={{ color: e.color }} className="whitespace-nowrap">
              {e.k.toUpperCase().replace('EMA', 'EMA ')}
              <span className={`ml-1 ${e.warming ? 'text-[var(--txt-muted)]' : 'text-[var(--txt-primary)]'}`}>
                {e.value != null
                  ? e.value.toLocaleString(undefined, { maximumFractionDigits: e.value < 1 ? 6 : 2 })
                  : `warming (${(candles?.length || 0)}/${e.period})`}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
